import React from "react";
import {DayTypes} from "./MatchSchedule";
import {IMatchScheduleTableEvent} from "../OddsComparisonTable";
import {EventStatus, IMaxOdd, oddsFormats} from "../../utils/types";
import {AccordionItem, AccordionItemButton, AccordionItemHeading, AccordionItemPanel} from "react-accessible-accordion";
import {_t} from "../../utils/translations";
import {Outcomes} from "../../utils/outcome";
import OddsButtonWithOperatorLogoAndOutcome from "../Buttons/OddsButtonWithOperatorLogoAndOutcome";
import {IMatchScheduleResponse} from "../../api/general";
import bn from "../../utils/bemnames";
import {getOrdinalNumberSuffix} from "../../utils/helpers";
import {useGlobalState} from "../../contexts/GlobalState";
import {IOutLinks} from "../../api/match-center";

const bem = bn.create("match_schedule");

interface IProps {
  matchScheduleData: IMatchScheduleResponse,
  isMobile: boolean,
  builderComponentId?: number,
  oddsFormat: oddsFormats,
  containerWidth: number,
  outLinks: IOutLinks
}

const MatchScheduleEventsContainer: React.FC<IProps> = (props) => {
  const {
    matchScheduleData,
    isMobile,
    builderComponentId,
    oddsFormat,
    containerWidth,
    outLinks
  } = props

  const {globalState} = useGlobalState();

  const groupedEvents = () => {
    let events: {
      [DayTypes.LIVE]?: IMatchScheduleTableEvent[],
      [DayTypes.TODAY]?: IMatchScheduleTableEvent[],
      [DayTypes.TOMORROW]?: IMatchScheduleTableEvent[]
    } = {
      [DayTypes.LIVE]: [],
      [DayTypes.TODAY]: [],
      [DayTypes.TOMORROW]: []
    }

    let today = new Date()
    let tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    matchScheduleData?.events.forEach((item) => {
      let startTime = new Date(item.startTime).toDateString()

      if (item.status === EventStatus.Inprogress)
        events[DayTypes.LIVE]?.push(item)
      else if (startTime === today.toDateString())
        events[DayTypes.TODAY]?.push(item)
      else if (startTime === tomorrow.toDateString())
        events[DayTypes.TOMORROW]?.push(item)
      else {
        if (events.hasOwnProperty(startTime))
          events[startTime].push(item)
        else Object.assign(events, {[startTime]: [item]})
      }
    })

    if (events[DayTypes.LIVE]?.length === 0)
      delete events[DayTypes.LIVE]
    if (events[DayTypes.TODAY]?.length === 0)
      delete events[DayTypes.TODAY]
    if (events[DayTypes.TOMORROW]?.length === 0)
      delete events[DayTypes.TOMORROW]

    return events
  }

  const getEventsList = (): JSX.Element[] => {
    let eventContainers: JSX.Element[] = []
    let eventGroups = groupedEvents()

    for (const day in eventGroups) {
      eventContainers.push((
        <div className={bem.e('events-container')} key={day}>
          <AccordionItem>
            <AccordionItemHeading>
              <AccordionItemButton>
                <div className={bem.e('events-container__header')}>{formatStartDate(day)}</div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              <div className={bem.e('events-container__events-list')}>
                {eventGroups[day].map((item: IMatchScheduleTableEvent, eventsIndex: number) => {
                  return (
                    <div className={bem.e('events-container__event-container')} key={`events_${eventsIndex}`}>
                      <div className={bem.e('events-container__event-data')}>
                        <div className={bem.e('events-container__event-data__name')}>
                          <span>{item.homeName}</span>
                          {!isMobile && (<span> - </span>)}
                          <span>{item.awayName}</span>
                        </div>
                        <div className={bem.e('events-container__event-data__start-time')}>
                          {formatStartTime(item)}
                        </div>
                      </div>
                      {item.maxOdds.length > 0 && item.maxOdds[0].odds !== null && (
                        <div className={bem.e('events-container__odds-data')}>
                          {isMobile && !isSmallDisplay() && (
                            <span>{_t('Best Odds')}</span>
                          )}
                          {item.maxOdds.map((oddsItem: IMaxOdd, index: number) => {
                            if (!builderComponentId || !globalState.widget)
                              return null

                            let outcomeIndex = Object.values(Outcomes).indexOf(oddsItem.outcomeName as unknown as Outcomes)
                            if (outcomeIndex === -1)
                              return null
                            let outcomeKey = Object.keys(Outcomes)[outcomeIndex]

                            return (
                              <OddsButtonWithOperatorLogoAndOutcome
                                oddsLine={oddsItem.odds}
                                widgetId={globalState.widget?.id}
                                outLinkFolder={outLinks.outLinkFolder}
                                eventId={item.id}
                                builderComponentId={builderComponentId}
                                provider={oddsItem.provider}
                                outcome={Outcomes[outcomeKey]}
                                oddsFormat={oddsFormat}
                                isMobile={isMobile}
                                eventStatus={item.status}
                                oddsPrev={oddsItem.oddsPrev}
                                key={`odds_btn_${index}`}
                              />
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </AccordionItemPanel>
          </AccordionItem>
        </div>
      ))
    }

    return eventContainers
  }

  const isSmallDisplay = (): boolean => {
    return containerWidth < 430
  }

  const formatStartDate = (day: string) => {
    if (Object.values(DayTypes).includes(day as unknown as DayTypes)) {
      let dayLabel = day
      if (Object.hasOwn(DayTypes, day))
        dayLabel = _t(dayLabel)
      return (
        <span style={{textTransform: "uppercase"}}>{dayLabel}</span>
      )
    } else {
      let date = new Date(day)
      let dayNumber = date.getDate()
      let daySuffix = getOrdinalNumberSuffix(dayNumber.toString())
      let month = date.toLocaleDateString('en-US', {month: 'long'})

      return (
        <span>{dayNumber}<sup>{daySuffix}</sup> {_t(month)}</span>
      )
    }
  }

  const formatStartTime = (event: IMatchScheduleTableEvent): JSX.Element | null => {
    let startTimeDay = ''
    let daySuffixElement = null

    let today = new Date()
    let tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)

    let startTimeObject = new Date(event.startTime)
    let startTimeDateString = startTimeObject.toDateString()
    let startTimeString = startTimeObject.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    })

    if (event.status === EventStatus.Inprogress) {
      return (
        <span className={bem.e('events-container__event-data__start-time live')}>
          {_t(DayTypes.LIVE)} {event.gameMoment}
        </span>
      )
    } else {
      if (startTimeDateString === today.toDateString())
        startTimeDay = _t(DayTypes.TODAY)
      else if (startTimeDateString === tomorrow.toDateString())
        startTimeDay = _t(DayTypes.TOMORROW)
      else {
        let weekday = startTimeObject.toLocaleDateString('en-US', {weekday: 'long'})
        let dayNumber = startTimeObject.getDate()
        let daySuffix = getOrdinalNumberSuffix(dayNumber.toString())
        daySuffixElement = <sup>{daySuffix}</sup>
        startTimeDay = `${_t(weekday)} ${dayNumber}`
      }

      return (
        <span>{startTimeDay}{daySuffixElement !== null && daySuffixElement} {_t('at')} {startTimeString}</span>
      )
    }
  }

  return (
    <React.Fragment>
      {getEventsList()}
    </React.Fragment>
  )
}

export default MatchScheduleEventsContainer
